import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  Link2, Unlink, TestTube2, CheckCircle2, XCircle, RefreshCw,
  Save, Info, ArrowLeftRight, Clock, FileText, Webhook, Copy,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, ConfirmModal, Paginator,
} from "../ui";
import "./settings.scss";

const ZohoSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Connection state
  const [status, setStatus] = useState({
    isConnected: false,
    isActive: false,
    zohoOrgName: "",
    syncDirection: "lms_to_zoho",
    autoSyncEnabled: false,
    syncIntervalMinutes: 30,
    lastSyncAt: null,
    lastSyncRecordCount: 0,
    lastSyncError: null,
  });

  // Connect form (only shown when not connected)
  const [connectForm, setConnectForm] = useState({
    accountsDomain: "https://accounts.zoho.in",
  });

  // Sync logs
  const [syncLogs, setSyncLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Status mapping
  const [statusMapping, setStatusMapping] = useState({});

  // Webhook
  const [webhookUrl, setWebhookUrl] = useState("");
  const [generatingWebhook, setGeneratingWebhook] = useState(false);

  const ZOHO_DOMAINS = [
    { value: "https://accounts.zoho.in", label: "India (.in)" },
    { value: "https://accounts.zoho.com", label: "US (.com)" },
    { value: "https://accounts.zoho.eu", label: "EU (.eu)" },
    { value: "https://accounts.zoho.com.au", label: "Australia (.com.au)" },
    { value: "https://accounts.zoho.jp", label: "Japan (.jp)" },
  ];

  const LMS_STATUSES = ["Enquired", "Confirmed", "Dropped", "Postponed", "Closed", "Converted"];

  // ── Load status ────────────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/zoho/status");
      setStatus({
        isConnected: data.connected || data.isActive || false,
        isActive: data.isActive || false,
        zohoOrgName: data.zohoOrgName || "",
        syncDirection: data.syncDirection || "lms_to_zoho",
        autoSyncEnabled: data.autoSyncEnabled || false,
        syncIntervalMinutes: data.syncIntervalMinutes || 30,
        lastSyncAt: data.lastSyncAt,
        lastSyncRecordCount: data.lastSyncRecordCount || 0,
        lastSyncError: data.lastSyncError,
      });
      if (data.statusMapping) {
        setStatusMapping(data.statusMapping);
      }
    } catch {
      // Not connected yet — that's fine
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // ── Connect ────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.post("/zoho/connect", {
        accountsDomain: connectForm.accountsDomain,
      });
      if (data.consentUrl) {
        window.location.href = data.consentUrl;
      }
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to start Zoho connection.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSaving(false);
  };

  // ── Check for OAuth callback result ────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zohoStatus = params.get("zoho");
    if (zohoStatus === "connected") {
      setToast({ show: true, message: "Zoho CRM connected successfully!", type: "success" });
      loadStatus();
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (zohoStatus === "failed") {
      const reason = params.get("reason") || "Connection failed";
      setToast({ show: true, message: `Zoho connection failed: ${reason}`, type: "error" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadStatus]);

  // ── Disconnect ─────────────────────────────────────────────────────────
  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await apiClient.post("/zoho/disconnect");
      setToast({ show: true, message: "Zoho CRM disconnected.", type: "success" });
      setStatus(s => ({ ...s, isConnected: false, isActive: false }));
      setConnectForm({ accountsDomain: "https://accounts.zoho.in" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to disconnect.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setDisconnecting(false);
    setShowDisconnect(false);
  };

  // ── Test Connection ────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/zoho/test-connection");
      setTestResult({ success: true, message: data.message || "Connection successful" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Connection test failed.";
      setTestResult({ success: false, message: msg });
    }
    setTesting(false);
  };

  // ── Save Settings ──────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/zoho/settings", {
        syncDirection: status.syncDirection,
        autoSyncEnabled: status.autoSyncEnabled,
        syncIntervalMinutes: status.syncIntervalMinutes,
        statusMapping,
      });
      setToast({ show: true, message: "Zoho settings saved!", type: "success" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to save settings.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSaving(false);
  };

  // ── Sync Now ───────────────────────────────────────────────────────────
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/zoho/sync-now");
      setToast({ show: true, message: data.message || "Sync job queued!", type: "success" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to start sync.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSyncing(false);
  };

  // ── Generate Webhook Secret ─────────────────────────────────────────────
  const handleGenerateWebhook = async () => {
    setGeneratingWebhook(true);
    try {
      const { data } = await apiClient.post("/zoho/webhook-secret");
      setWebhookUrl(data.webhookUrl || "");
      setToast({ show: true, message: "Webhook URL generated! Copy it to Zoho CRM.", type: "success" });
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to generate webhook.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setGeneratingWebhook(false);
  };

  const handleCopyWebhook = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl).then(() => {
        setToast({ show: true, message: "Webhook URL copied!", type: "success" });
      });
    }
  };

  // ── Load Sync Logs ─────────────────────────────────────────────────────
  const loadSyncLogs = useCallback(async () => {
    if (!status.isConnected) return;
    setLoadingLogs(true);
    try {
      const { data } = await apiClient.get("/zoho/sync-logs", {
        params: { page: logsPage, limit: 20 },
      });
      setSyncLogs(data.logs || []);
      setLogsTotalPages(data.totalPages || 1);
    } catch {
      // silent
    }
    setLoadingLogs(false);
  }, [status.isConnected, logsPage]);

  useEffect(() => {
    if (status.isConnected) loadSyncLogs();
  }, [status.isConnected, loadSyncLogs]);

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner size="sm" /> Loading Zoho settings…
      </div>
    );
  }

  return (
    <div className="zoho-settings">
      {/* ── Connection Card ─────────────────────────────────────────────── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Link2 size={18} /> Zoho CRM Connection
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Connect your Zoho CRM account to sync enquiries as leads automatically.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {status.isConnected ? (
            <div className="zoom-connected-info">
              <div className="zoom-connected-info__status">
                <CheckCircle2 size={20} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                <div>
                  <strong>Zoho CRM Connected</strong>
                  {status.zohoOrgName && (
                    <p className="mb-0">{status.zohoOrgName}</p>
                  )}
                </div>
              </div>
              <div className="zoom-connected-info__actions">
                <AppButton
                  variant="outline"
                  size="sm"
                  onClick={handleSyncNow}
                  disabled={syncing}
                >
                  <RefreshCw size={14} className={syncing ? "spin" : ""} />{" "}
                  {syncing ? "Syncing…" : "Pull Leads"}
                </AppButton>
                <AppButton
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing}
                >
                  <TestTube2 size={14} /> {testing ? "Testing…" : "Test Connection"}
                </AppButton>
                <AppButton
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowDisconnect(true)}
                >
                  <Unlink size={14} /> Disconnect
                </AppButton>
              </div>
              {testResult && (
                <div style={{
                  marginTop: 8,
                  fontSize: '0.8125rem',
                  color: testResult.success ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  {testResult.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{" "}
                  {testResult.message}
                </div>
              )}
            </div>
          ) : (
            <div className="zoom-connect-prompt">
              <p className="settings-card-desc mb-3">
                Connect your Zoho CRM account to sync enquiries as leads automatically.
              </p>
              <div className="eq-field-grid">
                <div className="eq-field">
                  <label>Zoho Data Center</label>
                  <select
                    value={connectForm.accountsDomain}
                    onChange={(e) => setConnectForm(f => ({ ...f, accountsDomain: e.target.value }))}
                  >
                    {ZOHO_DOMAINS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <AppButton onClick={handleConnect} disabled={saving}>
                  <Link2 size={16} /> {saving ? "Connecting…" : "Connect Zoho CRM"}
                </AppButton>
              </div>
              <div className="mt-2">
                <small className="settings-card-desc">
                  <Info size={12} /> You'll be redirected to Zoho to authorize access to your CRM account.
                </small>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Sync Settings (only when connected) ────────────────────────── */}
      {status.isConnected && (
        <>
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <ArrowLeftRight size={18} /> Sync Settings
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Configure how enquiries sync with Zoho CRM leads.
              </p>
            </AppCardHeader>
            <AppCardContent>
              <div className="eq-field-grid">
                <div className="eq-field">
                  <label>Sync Direction</label>
                  <select
                    value={status.syncDirection}
                    onChange={(e) => setStatus(s => ({ ...s, syncDirection: e.target.value }))}
                  >
                    <option value="lms_to_zoho">LMS → Zoho (One-way push)</option>
                    <option value="zoho_to_lms">Zoho → LMS (One-way pull)</option>
                    <option value="bidirectional">Bidirectional</option>
                  </select>
                </div>
                <div className="eq-field">
                  <label>Auto Sync</label>
                  <select
                    value={status.autoSyncEnabled ? "enabled" : "disabled"}
                    onChange={(e) => setStatus(s => ({ ...s, autoSyncEnabled: e.target.value === "enabled" }))}
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
                <div className="eq-field">
                  <label>Sync Interval (minutes)</label>
                  <select
                    value={status.syncIntervalMinutes}
                    onChange={(e) => setStatus(s => ({ ...s, syncIntervalMinutes: Number(e.target.value) }))}
                  >
                    <option value={1}>Every 1 minute (Real-time)</option>
                    <option value={5}>Every 5 minutes</option>
                    <option value={15}>Every 15 minutes</option>
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every hour</option>
                    <option value={360}>Every 6 hours</option>
                    <option value={1440}>Once daily</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
                <AppButton onClick={handleSaveSettings} disabled={saving}>
                  <Save size={16} /> {saving ? "Saving…" : "Save Settings"}
                </AppButton>
                <AppButton variant="outline" onClick={handleSyncNow} disabled={syncing}>
                  <RefreshCw size={16} className={syncing ? "spin" : ""} />{" "}
                  {syncing ? "Queuing…" : "Sync Now"}
                </AppButton>
              </div>

              {status.lastSyncAt && (
                <div className="sync-last-time">
                  <Clock size={12} /> Last sync:{" "}
                  {new Date(status.lastSyncAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  {status.lastSyncRecordCount > 0 && ` (${status.lastSyncRecordCount} records)`}
                  {status.lastSyncError && (
                    <span style={{ color: 'var(--color-danger)' }}>
                      <XCircle size={12} /> {status.lastSyncError}
                    </span>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>

          {/* ── Status Mapping ───────────────────────────────────────────── */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <ArrowLeftRight size={18} /> Status Mapping
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Map LMS enquiry statuses to Zoho CRM lead statuses.
              </p>
            </AppCardHeader>
            <AppCardContent>
              <div className="eq-field-grid">
                {LMS_STATUSES.map((lmsStatus) => (
                  <div className="eq-field" key={lmsStatus}>
                    <label>{lmsStatus}</label>
                    <input
                      placeholder={`Zoho status for "${lmsStatus}"`}
                      value={statusMapping[lmsStatus] || ""}
                      onChange={(e) =>
                        setStatusMapping((m) => ({ ...m, [lmsStatus]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <small className="settings-card-desc">
                  <Info size={12} /> Leave blank to use defaults (Enquired→New, Confirmed→Contacted, Converted→Converted, etc.)
                </small>
              </div>
            </AppCardContent>
          </AppCard>

          {/* ── Sync Logs ────────────────────────────────────────────────── */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <FileText size={18} /> Manual Sync Logs
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Logs from manual "Pull Leads" syncs. Auto-sync runs silently and only updates the last sync time above.
              </p>
            </AppCardHeader>
            <AppCardContent>
              {loadingLogs ? (
                <div className="text-center py-3">
                  <Spinner size="sm" /> Loading logs…
                </div>
              ) : syncLogs.length === 0 ? (
                <p className="settings-card-desc">No manual sync activity yet. Use "Pull Leads" to trigger a sync.</p>
              ) : (
                <>
                  <div className="eq-table-wrapper">
                    <table className="eq-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Action</th>
                          <th>Direction</th>
                          <th>Status</th>
                          <th>Records</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncLogs.map((log) => (
                          <tr key={log._id}>
                            <td className="eq-cell-date">
                              {new Date(log.createdAt).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                dateStyle: "short",
                                timeStyle: "short",
                              })}
                            </td>
                            <td>{log.action}</td>
                            <td>
                              <span className={`sync-badge sync-badge--direction`}>
                                {log.direction === "outbound" ? "LMS → Zoho" : "Zoho → LMS"}
                              </span>
                            </td>
                            <td>
                              <span className={`sync-badge sync-badge--${log.status === "success" ? "success" : log.status === "failed" ? "failed" : "warning"}`}>
                                {log.status}
                              </span>
                            </td>
                            <td>{log.recordCount || "-"}</td>
                            <td className="small" style={{ color: 'var(--text-secondary)' }}>
                              {log.errorMessage || (log.details ? log.details.slice(0, 80) : "-")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {logsTotalPages > 1 && (
                    <Paginator
                      page={logsPage}
                      totalPages={logsTotalPages}
                      onChange={setLogsPage}
                      infoLabel="logs"
                    />
                  )}
                </>
              )}
            </AppCardContent>
          </AppCard>

          {/* ── Webhook (Real-time Leads) ───────────────────────────────── */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <Webhook size={18} /> Real-Time Webhook
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Enable real-time lead delivery from Zoho CRM. New leads will appear instantly with a notification sound.
              </p>
            </AppCardHeader>
            <AppCardContent>
              <p className="settings-card-desc mb-2" style={{ fontSize: "0.85rem" }}>
                Generate a webhook URL and paste it in <strong>Zoho CRM → Settings → Automation → Webhook</strong>.
                Set the module to <strong>Leads</strong> and trigger on <strong>Create / Edit</strong>.
              </p>

              {webhookUrl ? (
                <div className="invite-url-row mb-2">
                  <input
                    readOnly
                    value={webhookUrl}
                    className="invite-url-input"
                    style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                    onClick={(e) => e.target.select()}
                  />
                  <AppButton variant="outline" size="sm" onClick={handleCopyWebhook}>
                    <Copy size={14} /> Copy
                  </AppButton>
                </div>
              ) : (
                <AppButton
                  variant="outline"
                  onClick={handleGenerateWebhook}
                  disabled={generatingWebhook}
                >
                  <Webhook size={16} /> {generatingWebhook ? "Generating…" : "Generate Webhook URL"}
                </AppButton>
              )}

              <div className="mt-2">
                <small className="settings-card-desc">
                  <Info size={12} /> Each time you generate a new URL, the previous one is invalidated.
                  When Zoho sends a lead event, it'll appear in your enquiries list instantly with a notification sound.
                </small>
              </div>
            </AppCardContent>
          </AppCard>
        </>
      )}

      <ConfirmModal
        show={showDisconnect}
        title="Disconnect Zoho CRM"
        message="This will remove your Zoho CRM credentials. Existing synced data in your enquiries will not be deleted. Are you sure?"
        onConfirm={handleDisconnect}
        onHide={() => setShowDisconnect(false)}
        isLoading={disconnecting}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ZohoSettings;
